import PropTypes from 'prop-types';
import { alpha, Box, Card, CardContent, Chip, Typography } from '@mui/material';
import {
  LABEL_META,
  TRANSITION_FAVORABLE_LABELS,
  convArrows,
  getTransitionAccent,
} from './shared';

const notableShape = PropTypes.shape({
  ticker: PropTypes.string,
  behavior_label: PropTypes.string,
  prev_label: PropTypes.string,
  conviction_delta: PropTypes.number,
});

const TransitionsBanner = ({ t, transitions }) => {
  if (!transitions?.labels_changed_today) return null;

  const notable = transitions.notable || [];
  const upgrades = notable.filter((item) => (
    TRANSITION_FAVORABLE_LABELS.has(item.behavior_label)
      && !TRANSITION_FAVORABLE_LABELS.has(item.prev_label)
  ));
  const downgrades = notable.filter((item) => (
    item.behavior_label === 'FAKE_SUPPORT_OR_EXIT'
      || (item.prev_label === 'ACTIVE_MARKUP' && item.behavior_label !== 'ACTIVE_MARKUP')
  ));
  const prioritizedNotable = [
    ...upgrades.slice(0, 5),
    ...downgrades.slice(0, 5),
    ...notable.filter((item) => !upgrades.slice(0, 5).includes(item) && !downgrades.slice(0, 5).includes(item)),
  ].slice(0, 10);

  return (
    <Card sx={{ mb: 2, border: '1px solid', borderColor: alpha('#7c3aed', 0.15), bgcolor: alpha('#7c3aed', 0.02), boxShadow: 'none' }}>
      <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: notable.length > 0 ? 0.75 : 0, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#7c3aed' }}>
            {t('behavior.transition.banner')}
          </Typography>
          <Chip
            label={t('behavior.transition.changed', { count: transitions.labels_changed_today })}
            size="small"
            sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha('#7c3aed', 0.1), color: '#7c3aed' }}
          />
          {transitions.conviction_up > 0 && (
            <Chip
              label={`▲ ${transitions.conviction_up} ${t('behavior.transition.convUp')}`}
              size="small"
              sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha('#10b981', 0.1), color: '#10b981' }}
            />
          )}
          {transitions.conviction_down > 0 && (
            <Chip
              label={`▼ ${transitions.conviction_down} ${t('behavior.transition.convDown')}`}
              size="small"
              sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha('#ef4444', 0.1), color: '#ef4444' }}
            />
          )}
        </Box>
        {notable.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {prioritizedNotable.map((item) => {
              const isUp = upgrades.includes(item);
              const isDown = downgrades.includes(item);
              const accent = getTransitionAccent(isUp, isDown);
              const prevMeta = LABEL_META[item.prev_label] || LABEL_META.NEUTRAL;
              const currentMeta = LABEL_META[item.behavior_label] || LABEL_META.NEUTRAL;
              const arrows = convArrows(item.conviction_delta);

              return (
                <Box key={item.ticker} sx={{ display: 'flex', alignItems: 'center', gap: 0.3, px: 0.75, py: 0.2, borderRadius: 0.75, bgcolor: alpha(accent, 0.06), border: `1px solid ${alpha(accent, 0.15)}` }}>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#1e293b' }}>{item.ticker}</Typography>
                  <Typography sx={{ fontSize: '0.58rem', color: prevMeta.color, fontWeight: 600 }}>
                    {t(`behavior.labels.${item.prev_label || 'NEUTRAL'}.short`)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.58rem', color: '#cbd5e1' }}>→</Typography>
                  <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: currentMeta.color }}>
                    {t(`behavior.labels.${item.behavior_label || 'NEUTRAL'}.short`)}
                  </Typography>
                  {arrows && (
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, fontFamily: 'monospace', color: item.conviction_delta > 0 ? '#10b981' : '#ef4444' }}>
                      {arrows}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

TransitionsBanner.propTypes = {
  t: PropTypes.func.isRequired,
  transitions: PropTypes.shape({
    labels_changed_today: PropTypes.number,
    conviction_up: PropTypes.number,
    conviction_down: PropTypes.number,
    notable: PropTypes.arrayOf(notableShape),
  }),
};

TransitionsBanner.defaultProps = {
  transitions: null,
};

export default TransitionsBanner;
